<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 650 : 'auto'"
  >
    <v-card class="fix-overlaps-dialog">
      <!-- Header -->
      <v-card-title class="dialog-header pa-5">
        <div class="d-flex align-center justify-space-between w-100">
          <div class="d-flex align-center">
            <v-icon size="28" color="warning" class="mr-3">mdi-wrench</v-icon>
            <span class="text-h5 font-weight-bold">{{ $t('routes.pod.menuSubtitles.fixOverlappingSubtitles') }}</span>
          </div>
          <v-btn icon variant="text" size="small" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-5">
        <!-- Warning Alert -->
        <v-alert
          type="warning"
          variant="tonal"
          class="mb-5 warning-alert"
          prominent
        >
          <template v-slot:prepend>
            <v-icon size="32">mdi-alert-circle</v-icon>
          </template>

          <div class="alert-content">
            <div class="text-h6 font-weight-bold mb-2">
              {{ $t('routes.pod.menuSubtitles.overlappingSubtitlesDetected', warnings.length) }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              {{ $t('routes.pod.menuSubtitles.overlappingSubtitlesInfo') }}
            </div>

            <!-- Expandable Details -->
            <v-expand-transition>
              <div v-show="showDetails" class="mt-4">
                <v-divider class="mb-3"></v-divider>
                <div class="details-list">
                  <div
                    v-for="(warning, index) in warnings"
                    :key="index"
                    class="detail-item text-body-2 mb-2"
                  >
                    <v-icon size="16" class="mr-2">mdi-circle-small</v-icon>
                    {{ formatOverlapMessage(warning) }}
                  </div>
                </div>
              </div>
            </v-expand-transition>

            <v-btn
              variant="text"
              size="small"
              class="mt-2 text-none"
              @click="showDetails = !showDetails"
            >
              {{ showDetails ? $t('common.hide') : $t('common.show') }} details
              <v-icon size="18" class="ml-1">
                {{ showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
          </div>
        </v-alert>

        <!-- Strategy Selection -->
        <div class="strategy-section">
          <div class="text-subtitle-1 font-weight-bold mb-4">
            {{ $t('routes.pod.menuSubtitles.chooseFix') }}:
          </div>

          <!-- Split Option -->
          <v-card
            variant="outlined"
            class="strategy-card mb-3"
            :class="{ 'strategy-card--selected': selectedStrategy === 'split' }"
            @click="selectStrategy('split')"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="primary" size="56" class="mr-4 strategy-icon">
                  <v-icon size="28" color="white">mdi-call-split</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="text-h6 font-weight-bold mb-1">
                    {{ $t('routes.pod.menuSubtitles.splitOverlaps') }}
                    Split overlaps
                  </div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('routes.pod.menuSubtitles.adjustTimecodes') }}
                  </div>
                </div>

                <v-radio
                  :model-value="selectedStrategy"
                  value="split"
                  color="primary"
                  class="ml-3"
                ></v-radio>
              </div>
            </v-card-text>
          </v-card>

          <!-- Merge Option -->
          <v-card
            variant="outlined"
            class="strategy-card"
            :class="{ 'strategy-card--selected': selectedStrategy === 'merge' }"
            @click="selectStrategy('merge')"
          >
            <v-card-text class="pa-4">
              <div class="d-flex align-center">
                <v-avatar color="secondary" size="56" class="mr-4 strategy-icon">
                  <v-icon size="28" color="white">mdi-merge</v-icon>
                </v-avatar>

                <div class="flex-grow-1">
                  <div class="text-h6 font-weight-bold mb-1">
                    {{ $t('routes.pod.menuSubtitles.mergeOverlaps') }}
                  </div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('routes.pod.menuSubtitles.combineOverlapping') }}
                  </div>
                </div>

                <v-radio
                  :model-value="selectedStrategy"
                  value="merge"
                  color="primary"
                  class="ml-3"
                ></v-radio>
              </div>
            </v-card-text>
          </v-card>
        </div>

        <!-- Preview Section -->
        <v-expand-transition>
          <div v-if="selectedStrategy && previewChanges.length > 0" class="preview-section mt-5">
            <div class="d-flex align-center mb-3">
              <v-icon size="20" color="primary" class="mr-2">mdi-file-eye-outline</v-icon>
              <span class="text-subtitle-1 font-weight-bold">{{ $t('routes.pod.menuSubtitles.previewChanges') }}</span>
            </div>

            <v-card variant="tonal" class="preview-card">
              <v-card-text class="pa-4">
                <div class="text-body-2 mb-3">
                  <strong>{{ previewDescription }}</strong>
                </div>

                <div class="preview-changes-list">
                  <div
                    v-for="(change, index) in previewChanges"
                    :key="index"
                    class="preview-change-item text-body-2 mb-2 pa-3"
                  >
                    <v-icon size="14" class="mr-2" color="warning">mdi-chevron-right</v-icon>
                    {{ change }}
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-expand-transition>

        <!-- Error/Success Messages -->
        <v-expand-transition>
          <div v-if="errorMessage || successMessage" class="mt-4">
            <v-alert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              density="compact"
              closable
              class="mb-2"
            >
              {{ errorMessage }}
            </v-alert>
            <v-alert
              v-if="successMessage"
              type="success"
              variant="tonal"
              density="compact"
            >
              {{ successMessage }}
            </v-alert>
          </div>
        </v-expand-transition>
      </v-card-text>

      <v-divider></v-divider>

      <!-- Actions -->
      <v-card-actions class="pa-5">
        <v-btn
          variant="tonal"
          @click="handleKeepOverlaps"
          class="text-none"
          size="large"
        >
          {{ $t('routes.pod.menuSubtitles.keepOverlaps') }}
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          @click="dialog = false"
          class="text-none mr-2"
          size="large"
        >
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!selectedStrategy || preventDoubleClick"
          @click="applyFix"
          class="text-none px-8"
          size="large"
        >
          <v-icon size="20" class="mr-2">mdi-check-circle</v-icon>
          {{ $t('routes.pod.menuSubtitles.applyFix') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { useSubtitleStore } from '@/stores/SubtitleStore.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';

export default {
  name: 'FixOverlapsDialog',
  mixins: [DialogActionKeys, DialogModel],

  props: {
    warnings: {
      type: Array,
      default: () => [],
    },
    hasOverlaps: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['fix-overlaps', 'accept-overlaps'],

  data() {
    return {
      showDetails: false,
      selectedStrategy: null,
      previewDescription: '',
      previewChanges: [],
      errorMessage: '',
      successMessage: '',
      preventDoubleClick: false,
    };
  },

  watch: {
    selectedStrategy(newStrategy) {
      if (newStrategy) {
        this.generatePreview(newStrategy);
      }
    },
    dialog(isVisible) {
      if (!isVisible) {
        this.resetDialog();
      }
    },
  },

  methods: {
    selectStrategy(strategy) {
      this.selectedStrategy = strategy;
      this.errorMessage = '';
      this.successMessage = '';
    },

    generatePreview(strategy) {
      if (strategy === 'split') {
        this.previewDescription = this.$t('routes.pod.menuSubtitles.splittingDescription');
        this.previewChanges = this.generateSplitPreview();
      } else if (strategy === 'merge') {
        this.previewDescription = this.$t('routes.pod.menuSubtitles.combiningDescription');
        this.previewChanges = this.generateMergePreview();
      }
    },

    generateSplitPreview() {
      const changes = [];
      const subtitleStore = useSubtitleStore();
      const subtitles = subtitleStore.getSubtitles;

      for (const warning of this.warnings) {
        if (warning.type === 'overlap') {
          const [id1, id2] = warning.ids;
          const sub1 = subtitles.find((s) => s.id === id1);
          const sub2 = subtitles.find((s) => s.id === id2);

          if (sub1 && sub2) {
            const overlapStart = Math.max(sub1.startTime, sub2.startTime);
            const overlapEnd = Math.min(sub1.endTime, sub2.endTime);
            const midPoint = overlapStart + Math.floor((overlapEnd - overlapStart) / 2);

            changes.push(this.$t('routes.pod.menuSubtitles.endTimeChangeFromTo', {
              id: id1,
              oldEndTime: this.formatTime(sub1.endTime),
              newEndTime: this.formatTime(midPoint)
            }));

            changes.push(this.$t('routes.pod.menuSubtitles.startTimeChangeFromTo', {
              id: id2,
              oldEndTime: this.formatTime(sub2.startTime),
              newEndTime: this.formatTime(midPoint)
            }));
          }
        }
      }

      return changes.length > 0 ? changes : [this.$t('routes.pod.menuSubtitles.allOverlapsToAdjustingTimecodes')];
    },

    generateMergePreview() {
      const changes = [];
      const subtitleStore = useSubtitleStore();
      const subtitles = subtitleStore.getSubtitles;
      const mergeGroups = new Map();

      for (const warning of this.warnings) {
        if (warning.type === 'overlap') {
          const [id1, id2] = warning.ids;

          let group1 = null;
          let group2 = null;

          for (const [key, group] of mergeGroups) {
            if (group.has(id1)) group1 = key;
            if (group.has(id2)) group2 = key;
          }

          if (!group1 && !group2) {
            mergeGroups.set(id1, new Set([id1, id2]));
          } else if (group1 && !group2) {
            mergeGroups.get(group1).add(id2);
          } else if (!group1 && group2) {
            mergeGroups.get(group2).add(id1);
          } else if (group1 && group2 && group1 !== group2) {
            const group = mergeGroups.get(group1);
            for (const id of mergeGroups.get(group2)) {
              group.add(id);
            }
            mergeGroups.delete(group2);
          }
        }
      }

      for (const group of mergeGroups.values()) {
        const ids = Array.from(group).sort((a, b) => a - b);
        const subs = ids.map((id) => subtitles.find((s) => s.id === id)).filter(Boolean);

        if (subs.length > 1) {
          const minStart = Math.min(...subs.map((s) => s.startTime));
          const maxEnd = Math.max(...subs.map((s) => s.endTime));
          const mergedText = subs.map((s) => s.text).join('\n');

          changes.push(this.$t('routes.pod.menuSubtitles.linesWillBeMerged', { lines: ids.join(', ') }));
          changes.push(`  ${this.$t('common.timeTitle')}: ${this.formatTime(minStart)} → ${this.formatTime(maxEnd)}`);
          changes.push(`  ${this.$t('routes.pod.menuSubtitles.textPreview')}: "${mergedText.length > 50 ? mergedText.substring(0, 50).replace(/\n/g, ' ↵ ') + '...' : mergedText.replace(/\n/g, ' ↵ ')}"`);
        }
      }

      const allMergedIds = new Set();
      for (const [, group] of mergeGroups) {
        for (const id of group) {
          allMergedIds.add(id);
        }
      }

      const nonOverlapping = subtitles.filter((s) => !allMergedIds.has(s.id));
      if (nonOverlapping.length > 0) {
        changes.push(this.$t('routes.pod.menuSubtitles.subsRemainUnchanged', { count: nonOverlapping.length }));
      }

      return changes.length > 0 ? changes : [this.$t('routes.pod.menuSubtitles.allOverlapsToCombined')];
    },

    formatTime(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const ms = milliseconds % 1000;

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
    },

    applyFix() {
      if (!this.selectedStrategy) {
        return;
      }

      this.preventDoubleClick = true;
      this.errorMessage = '';
      this.successMessage = '';

      setTimeout(() => {
        this.preventDoubleClick = false;
      }, 3000);

      try {
        this.$emit('fix-overlaps', this.selectedStrategy);
        this.successMessage = this.$t('routes.pod.menuSubtitles.overlapsFixed');

        setTimeout(() => {
          this.dialog = false;
        }, 1500);
      } catch (error) {
        console.log(error);
        this.errorMessage = this.$t('routes.pod.menuSubtitles.filedToFixOverlaps');
        this.preventDoubleClick = false;
      }
    },

    handleKeepOverlaps() {
      this.$emit('accept-overlaps');
      this.dialog = false;
    },

    resetDialog() {
      this.showDetails = false;
      this.selectedStrategy = null;
      this.previewDescription = '';
      this.previewChanges = [];
      this.errorMessage = '';
      this.successMessage = '';
      this.preventDoubleClick = false;
    },

    formatTimeFromMilliseconds(milliseconds) {
      const totalSeconds = Math.floor(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const ms = milliseconds % 1000;

      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(Math.floor(ms / 10)).padStart(2, '0')}`;
    },

    formatOverlapMessage(warning) {
      const overlapDurationFormatted = this.formatTimeFromMilliseconds(warning.overlapDuration);

      return this.$t('routes.pod.menuSubtitles.overlapWarningMessage', {
        p1: warning.messageParams.p1,
        p2: warning.messageParams.p2,
        p3: overlapDurationFormatted,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.fix-overlaps-dialog {
  .dialog-header {
    background: rgba(var(--v-theme-surface-variant), 0.5);
  }

  .warning-alert {
    border: 2px solid rgb(var(--v-theme-warning));

    .alert-content {
      width: 100%;
    }

    .details-list {
      .detail-item {
        display: flex;
        align-items: flex-start;
        padding: 6px 12px;
        background: rgba(var(--v-theme-warning), 0.08);
        border-radius: 6px;
        border-left: 3px solid rgb(var(--v-theme-warning));
      }
    }
  }

  .strategy-section {
    .strategy-card {
      cursor: pointer;
      transition: all 0.2s ease;
      border: 2px solid transparent;

      &:hover {
        border-color: rgba(var(--v-theme-primary), 0.4);
        background: rgba(var(--v-theme-primary), 0.04);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      }

      &--selected {
        border-color: rgb(var(--v-theme-primary));
        background: rgba(var(--v-theme-primary), 0.08);
        box-shadow: 0 4px 12px rgba(var(--v-theme-primary), 0.2);
      }

      .strategy-icon {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      }
    }
  }

  .preview-section {
    .preview-card {
      border: 1px solid rgba(var(--v-theme-primary), 0.2);

      .preview-changes-list {
        max-height: 250px;
        overflow-y: auto;

        .preview-change-item {
          display: flex;
          align-items: flex-start;
          background: rgba(var(--v-theme-warning), 0.08);
          border-radius: 6px;
          border-left: 3px solid rgb(var(--v-theme-warning));
        }
      }
    }
  }
}

// Dark theme adjustments
:deep(.v-theme--dark) {
  .fix-overlaps-dialog {
    .dialog-header {
      background: rgba(var(--v-theme-surface-variant), 0.3);
    }

    .warning-alert {
      background: rgba(var(--v-theme-warning), 0.12);

      .details-list .detail-item {
        background: rgba(255, 255, 255, 0.05);
      }
    }

    .strategy-card {
      &:hover {
        background: rgba(var(--v-theme-primary), 0.08);
      }

      &--selected {
        background: rgba(var(--v-theme-primary), 0.12);
      }
    }

    .preview-change-item {
      background: rgba(var(--v-theme-warning), 0.12) !important;
    }
  }
}

// Light theme adjustments
:deep(.v-theme--light) {
  .fix-overlaps-dialog {
    .dialog-header {
      background: rgba(var(--v-theme-surface-variant), 0.7);
    }

    .warning-alert {
      background: rgba(var(--v-theme-warning), 0.05);

      .details-list .detail-item {
        background: rgba(0, 0, 0, 0.03);
      }
    }

    .strategy-card {
      &:hover {
        background: rgba(var(--v-theme-primary), 0.03);
      }

      &--selected {
        background: rgba(var(--v-theme-primary), 0.06);
      }
    }

    .preview-change-item {
      background: rgba(var(--v-theme-warning), 0.06) !important;
    }
  }
}
</style>
